/**
 * Auth API — typed wrappers for all auth endpoints.
 */

import { z } from "zod";
import {
  api,
  apiRaw,
  apiVoid,
  bumpLogoutEpoch,
  extractCSRFToken,
  setCSRFToken,
} from "./client";
import { resetIdentity } from "@/lib/observability";
import {
  type LoginResponse,
  LoginResponseSchema,
  type MeResponse,
  MeResponseSchema,
  type MFASetupResponse,
  MFASetupResponseSchema,
  MFARequiredSchema,
  SafeLogoUrlsSchema,
  SessionConflictSchema,
  type WebAuthnCredential,
  WebAuthnCredentialSchema,
} from "./schemas";

// ── Request types ────────────────────────────────────────

interface LoginRequest {
  email: string;
  password: string;
  device_fingerprint: string;
  device_name: string;
}

interface MFALoginRequest {
  user_id: string;
  mfa_code: string;
  device_fingerprint: string;
  device_name: string;
  // Set true after the user acknowledges a session conflict that the MFA
  // step itself surfaces (e.g. another login appeared while they were
  // entering their TOTP code).
  force?: boolean;
}

interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

interface AcceptInvitationRequest {
  token: string;
  password: string;
  full_name: string;
}

// ── Login result discriminated union ─────────────────────

export type LoginResult =
  | { type: "success"; data: LoginResponse }
  | { type: "mfa_required"; user_id: string; mfa_methods: string[] }
  | { type: "session_conflict"; active_device: string; last_activity: string }
  | {
      type: "error";
      code: string;
      message: string;
      retryAfter?: number;
      // ISO-8601 deadline set by the backend on ``account_locked`` (423).
      // Used to render a precise unlock countdown instead of a generic
      // "try again later" message.
      lockedUntil?: string;
    };

// ── Auth API ─────────────────────────────────────────────

export async function login(request: LoginRequest): Promise<LoginResult> {
  const response = await apiRaw("/auth/login", {
    method: "POST",
    body: request,
  });

  const body: unknown = await response.json();

  if (response.ok) {
    extractCSRFToken(response);
    const data = LoginResponseSchema.parse(body);
    return { type: "success", data };
  }

  if (response.status === 403) {
    const parsed = MFARequiredSchema.safeParse(body);
    if (parsed.success) {
      return {
        type: "mfa_required",
        user_id: parsed.data.user_id,
        mfa_methods: parsed.data.mfa_methods,
      };
    }
  }

  if (response.status === 409) {
    const parsed = SessionConflictSchema.safeParse(body);
    if (parsed.success) {
      return {
        type: "session_conflict",
        active_device: parsed.data.active_device,
        last_activity: parsed.data.last_activity,
      };
    }
  }

  const errorBody = body as Record<string, unknown>;

  if (response.status === 429) {
    return {
      type: "error",
      code: "rate_limited",
      message: (errorBody["message"] as string) ?? "Too many requests. Please try again later.",
      ...(typeof errorBody["retry_after"] === "number" ? { retryAfter: errorBody["retry_after"] } : {}),
    };
  }

  return {
    type: "error",
    code: (errorBody["error"] as string) ?? "unknown",
    message: (errorBody["message"] as string) ?? "Login failed",
    ...(typeof errorBody["retry_after_seconds"] === "number"
      ? { retryAfter: errorBody["retry_after_seconds"] as number }
      : {}),
    ...(typeof errorBody["locked_until"] === "string"
      ? { lockedUntil: errorBody["locked_until"] as string }
      : {}),
  };
}

export async function loginConfirm(
  request: LoginRequest,
): Promise<LoginResult> {
  const response = await apiRaw("/auth/login/confirm", {
    method: "POST",
    body: request,
  });

  const body: unknown = await response.json();

  if (response.ok) {
    extractCSRFToken(response);
    const data = LoginResponseSchema.parse(body);
    return { type: "success", data };
  }

  if (response.status === 403) {
    const parsed = MFARequiredSchema.safeParse(body);
    if (parsed.success) {
      return {
        type: "mfa_required",
        user_id: parsed.data.user_id,
        mfa_methods: parsed.data.mfa_methods,
      };
    }
  }

  const errorBody = body as Record<string, unknown>;

  if (response.status === 429) {
    return {
      type: "error",
      code: "rate_limited",
      message: (errorBody["message"] as string) ?? "Too many requests. Please try again later.",
      ...(typeof errorBody["retry_after"] === "number" ? { retryAfter: errorBody["retry_after"] } : {}),
    };
  }

  return {
    type: "error",
    code: (errorBody["error"] as string) ?? "unknown",
    message: (errorBody["message"] as string) ?? "Login failed",
  };
}

export async function loginMFA(
  request: MFALoginRequest,
): Promise<LoginResult> {
  const response = await apiRaw("/auth/login/mfa", {
    method: "POST",
    body: request,
  });

  const body: unknown = await response.json();

  if (response.ok) {
    extractCSRFToken(response);
    const data = LoginResponseSchema.parse(body);
    return { type: "success", data };
  }

  // The MFA endpoint can now surface a session conflict — a parallel session
  // may have appeared between password verification and MFA submission.
  if (response.status === 409) {
    const parsed = SessionConflictSchema.safeParse(body);
    if (parsed.success) {
      return {
        type: "session_conflict",
        active_device: parsed.data.active_device,
        last_activity: parsed.data.last_activity,
      };
    }
  }

  const errorBody = body as Record<string, unknown>;

  if (response.status === 429) {
    return {
      type: "error",
      code: "rate_limited",
      message: (errorBody["message"] as string) ?? "Too many requests. Please try again later.",
      ...(typeof errorBody["retry_after"] === "number" ? { retryAfter: errorBody["retry_after"] } : {}),
    };
  }

  return {
    type: "error",
    code: (errorBody["error"] as string) ?? "unknown",
    message: (errorBody["message"] as string) ?? "MFA verification failed",
  };
}

export async function getMe(): Promise<MeResponse> {
  return api("/auth/me", MeResponseSchema);
}

export async function logout(): Promise<void> {
  // Abort any in-flight authenticated requests so they can't re-establish
  // session state on the way out. The CSRF token is intentionally NOT
  // cleared yet — the logout POST itself needs it to pass the backend's
  // CSRF guard. Clearing it first sent the request without
  // ``X-CSRF-Token``, the backend returned 403 ``csrf_error``, and
  // client.ts auto-refreshed → reissued fresh cookies → retried → 200.
  // The net effect was a brand-new session created moments before being
  // logged out, plus three round-trips instead of one.
  bumpLogoutEpoch();
  try {
    await apiVoid("/auth/logout", { method: "POST" });
  } catch {
    // Server-side invalidation is best-effort. Network failures here
    // leave the session row alive until the backend's sweeper reaps it;
    // the client-side teardown below still proceeds.
  }
  // Wipe PostHog distinct_id + groups before clearing the CSRF token so
  // the next session starts fresh (master decision #15). Routed through
  // ``resetIdentity()`` to keep the posthog-js import surface contained
  // inside ``src/lib/observability/`` — a lint rule to enforce that is
  // planned for PR-4. See plans/PLAN_POSTHOG_01_FOUNDATIONS.md
  // § "Logout flow integration".
  resetIdentity();
  setCSRFToken(null);
}

export async function setupMFA(): Promise<MFASetupResponse> {
  return api("/auth/mfa/setup", MFASetupResponseSchema, { method: "POST" });
}

const MFAVerifyResponseSchema = z.object({
  mfa_enabled: z.boolean(),
  recovery_codes: z.array(z.string()),
});

export type MFAVerifyResult = z.infer<typeof MFAVerifyResponseSchema>;

export async function verifyMFA(
  code: string,
): Promise<MFAVerifyResult> {
  return api("/auth/mfa/verify", MFAVerifyResponseSchema, {
    method: "POST",
    body: { code },
  });
}

const RecoveryCodesResponseSchema = z.object({
  recovery_codes: z.array(z.string()),
});

export async function regenerateRecoveryCodes(): Promise<{ recovery_codes: string[] }> {
  return api("/auth/mfa/recovery-codes", RecoveryCodesResponseSchema, {
    method: "POST",
  });
}

export async function changePassword(
  request: ChangePasswordRequest,
): Promise<void> {
  return apiVoid("/auth/password/change", {
    method: "POST",
    body: request,
  });
}

const AcceptInvitationResponseSchema = z.object({
  message: z.string(),
  email: z.string(),
});

/** Accept a password-flavor invitation. SSO invitations must be
 *  accepted via the OIDC callback flow — calling this with a
 *  ``requires_sso=true`` invitation throws an APIClientError with code
 *  ``invitation_requires_sso`` so the caller can redirect to the
 *  Microsoft sign-in URL instead. */
export async function acceptInvitation(
  request: AcceptInvitationRequest,
): Promise<{ message: string; email: string }> {
  return api("/invitations/accept", AcceptInvitationResponseSchema, {
    method: "POST",
    body: request,
  });
}

// ── Invitation preview ──────────────────────────────────

// Shared with CompanySchema: a malformed logo degrades to initials rather
// than failing the parse. Doubly important here — this shape backs the
// invitation preview, where an unparseable response would lock an invited
// user out of accepting at all.
const CompanyBrandSchema = z.object({
  name: z.string(),
  logo_urls: SafeLogoUrlsSchema,
});

const InvitationPreviewSchema = z.object({
  email: z.string(),
  invite_type: z.enum(["global_admin", "company_admin_onboarding", "regular_user"]),
  expires_at: z.string(),
  company: CompanyBrandSchema.nullable().optional(),
  // Drives the accept-page UI:
  //   requires_sso=true                    → SSO-only card
  //   requires_sso=false + sso_offered=true → dual-option card (user picks)
  //   requires_sso=false + sso_offered=false → password-only card
  // Defaults preserve the password-only path for backward-compat with any
  // older invitation that predates the SSO schema.
  requires_sso: z.boolean().default(false),
  idp_provider: z.string().nullable().default(null),
  sso_offered: z.boolean().default(false),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

/** Resolve an invitation token to a public-safe preview (company branding + role).
 *
 *  Throws ``APIClientError`` with stable codes:
 *    - ``invitation_not_found`` — wrong/garbled token
 *    - ``invitation_unusable``  — already accepted or revoked
 *    - ``invitation_expired``   — past TTL
 */
export async function getInvitationPreview(token: string): Promise<InvitationPreview> {
  const params = new URLSearchParams({ token });
  return api(`/invitations/preview?${params}`, InvitationPreviewSchema);
}

// ── Onboarding ──────────────────────────────────────────

const OnboardingStatusSchema = z.object({
  status: z.string(),
  steps: z.record(z.string(), z.boolean()),
  require_mfa: z.boolean(),
});

export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  return api("/auth/onboarding/status", OnboardingStatusSchema);
}

export async function updateOnboardingProfile(
  fullName: string,
): Promise<void> {
  return apiVoid("/auth/onboarding/profile", {
    method: "POST",
    body: { full_name: fullName },
  });
}

export async function completeOnboarding(): Promise<void> {
  return apiVoid("/auth/onboarding/complete", { method: "POST" });
}

// ── Password reset ──────────────────────────────────────

import {
  type PasswordResetRequestResponse,
  PasswordResetRequestResponseSchema,
  PasswordResetConfirmResponseSchema,
} from "./schemas";

export async function requestPasswordReset(
  email: string,
): Promise<PasswordResetRequestResponse> {
  return api("/auth/password/reset-request", PasswordResetRequestResponseSchema, {
    method: "POST",
    body: { email },
  });
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<{ message: string }> {
  return api("/auth/password/reset", PasswordResetConfirmResponseSchema, {
    method: "POST",
    body: { token, new_password: newPassword },
  });
}

// ── Profile management ─────────────────────────────────

export async function updateProfile(
  fullName: string,
): Promise<MeResponse> {
  return api("/auth/me", MeResponseSchema, {
    method: "PATCH",
    body: { full_name: fullName },
  });
}

/** Patch the user's personal timezone preference. Pass null to clear
 *  the preference and fall back to the company / GA default. */
export async function updateTimezone(
  timezone: string | null,
): Promise<MeResponse> {
  return api("/auth/me", MeResponseSchema, {
    method: "PATCH",
    body: { timezone },
  });
}

export async function disableMFA(code: string, password: string): Promise<void> {
  return apiVoid("/auth/mfa/disable", {
    method: "POST",
    body: { code, password },
  });
}

// ── Session management ─────────────────────────────────

import {
  type Session,
  SessionSchema,
} from "./schemas";

export async function listSessions(): Promise<Session[]> {
  return api("/auth/sessions", z.array(SessionSchema));
}

export async function revokeSession(sessionId: string): Promise<void> {
  return apiVoid(`/auth/sessions/${sessionId}`, { method: "DELETE" });
}

export async function revokeAllSessions(): Promise<void> {
  return apiVoid("/auth/sessions", { method: "DELETE" });
}

// ── WebAuthn (Global Admin only) ──────────────────────

// Raw JSON from navigator.credentials.create() / .get() — passed as-is to backend
type WebAuthnCredentialJSON = Record<string, unknown>;

const WebAuthnRegisterBeginSchema = z.record(z.string(), z.unknown());
const WebAuthnAuthBeginSchema = z.record(z.string(), z.unknown());

export async function beginWebAuthnRegistration(): Promise<WebAuthnCredentialJSON> {
  return api("/auth/webauthn/register/begin", WebAuthnRegisterBeginSchema, { method: "POST" });
}

const CompleteRegistrationResponseSchema = WebAuthnCredentialSchema;

export async function completeWebAuthnRegistration(
  credential: WebAuthnCredentialJSON,
  deviceName: string,
  setAsPrimary: boolean,
): Promise<WebAuthnCredential> {
  return api("/auth/webauthn/register/complete", CompleteRegistrationResponseSchema, {
    method: "POST",
    body: { credential, device_name: deviceName, set_as_primary: setAsPrimary },
  });
}

export async function beginWebAuthnAuthentication(
  userId: string,
): Promise<WebAuthnCredentialJSON> {
  return api("/auth/webauthn/authenticate/begin", WebAuthnAuthBeginSchema, {
    method: "POST",
    body: { user_id: userId },
  });
}

export async function completeWebAuthnAuthentication(
  userId: string,
  credential: WebAuthnCredentialJSON,
  deviceFingerprint: string,
  deviceName: string,
  force = false,
): Promise<LoginResult> {
  const response = await apiRaw("/auth/webauthn/authenticate/complete", {
    method: "POST",
    body: {
      user_id: userId,
      credential,
      device_fingerprint: deviceFingerprint,
      device_name: deviceName,
      force,
    },
  });

  const body: unknown = await response.json();

  if (response.ok) {
    extractCSRFToken(response);
    const data = LoginResponseSchema.parse(body);
    return { type: "success", data };
  }

  // The WebAuthn complete endpoint can now surface a session conflict —
  // WebAuthn is a primary authentication method, so it must enforce
  // single-device on its own.
  if (response.status === 409) {
    const parsed = SessionConflictSchema.safeParse(body);
    if (parsed.success) {
      return {
        type: "session_conflict",
        active_device: parsed.data.active_device,
        last_activity: parsed.data.last_activity,
      };
    }
  }

  const errorBody = body as Record<string, unknown>;
  if (response.status === 429) {
    return {
      type: "error",
      code: "rate_limited",
      message: (errorBody["message"] as string) ?? "Too many requests.",
      ...(typeof errorBody["retry_after"] === "number" ? { retryAfter: errorBody["retry_after"] } : {}),
    };
  }
  return {
    type: "error",
    code: (errorBody["error"] as string) ?? "webauthn_failed",
    message: (errorBody["message"] as string) ?? "WebAuthn authentication failed",
  };
}

export async function listWebAuthnCredentials(): Promise<WebAuthnCredential[]> {
  return api("/auth/webauthn/credentials", z.array(WebAuthnCredentialSchema));
}

export async function deleteWebAuthnCredential(id: string): Promise<void> {
  return apiVoid(`/auth/webauthn/credentials/${id}`, { method: "DELETE" });
}

export async function setWebAuthnCredentialPrimary(id: string): Promise<void> {
  return apiVoid(`/auth/webauthn/credentials/${id}/set-primary`, { method: "PATCH" });
}

export async function verifyTotpForWebAuthn(code: string): Promise<void> {
  return apiVoid("/auth/webauthn/verify-totp", {
    method: "POST",
    body: { code },
  });
}

// ── Login history ─────────────────────────────────────

import {
  type LoginHistoryResponse,
  LoginHistoryResponseSchema,
} from "./schemas";

export async function getLoginHistory(): Promise<LoginHistoryResponse> {
  return api("/auth/login-history", LoginHistoryResponseSchema);
}
